import * as React from "react";
import { motion, useReducedMotion, type Transition } from "framer-motion";

export default function A9DoDontRecap({ className = "w-full h-auto" }: { className?: string }) {
  const prefersReduced = useReducedMotion();

  const inView: Transition = prefersReduced
    ? { duration: 0 }
    : { type: "spring", stiffness: 300, damping: 26 };

  const float: Transition = prefersReduced
    ? { duration: 0 }
    : { duration: 3.2, repeat: Infinity, repeatType: "mirror", ease: "easeInOut" };

  const doItems: Item[] = [
    {
      title: "Use even, front lighting",
      desc: "Stand facing a window or soft light—avoid strong shadows.",
      Illustr: () => <LightGood float={float} />,
    },
    {
      title: "Hold at eye level",
      desc: "Keep the camera centered and an arm's length away.",
      Illustr: () => <EyeLevelGood float={float} />,
    },
    {
      title: "Neutral background",
      desc: "Plain wall; remove visual clutter so your face is the focus.",
      Illustr: () => <BgGood />,
    },
    {
      title: "Clean the lens",
      desc: "Wipe the camera glass to prevent haze or blur.",
      Illustr: () => <LensGood float={float} />,
    },
  ];

  const dontItems: Item[] = [
    {
      title: "Avoid backlighting",
      desc: "Bright light behind you will silhouette your face.",
      Illustr: () => <BacklightBad />,
    },
    {
      title: "No extreme angles",
      desc: "Too close or tilted distorts facial proportions.",
      Illustr: () => <AngleBad />,
    },
    {
      title: "Unobstruct the face",
      desc: "Move hair, glasses glare, or hands out of the way.",
      Illustr: () => <ObstructionBad />,
    },
    {
      title: "No filters/heavy makeup",
      desc: "Turn off beauty modes so skin tone/texture are true.",
      Illustr: () => <FilterBad />,
    },
  ];

  return (
    <section
      className={["rounded-2xl p-5 sm:p-6", className].join(" ")}
      style={{ border: '1px solid var(--rule)', background: 'var(--c-cream)', boxShadow: 'var(--sh-lifted)' }}
    >
      <header className="flex items-center justify-between gap-3 flex-wrap">
        <h2 className="text-lg font-semibold" style={{ color: 'var(--ink)' }}>Do / Don&apos;t Guidelines</h2>
        <p className="text-xs" style={{ color: 'var(--ink-muted)' }}>
          Quick reference for getting accurate scans.
        </p>
      </header>

      <div className="mt-4 grid gap-4 lg:grid-cols-2">
        {/* DO column */}
        <div className="rounded-xl p-4" style={{ border: '1px solid var(--rule)' }}>
          <div className="flex items-center gap-2">
            <CheckBadge />
            <h3 className="text-sm font-semibold tracking-wider uppercase" style={{ color: 'var(--ink)' }}>Do</h3>
          </div>
          <ul className="mt-3 grid gap-3">
            {doItems.map((it, i) => (
              <motion.li
                key={`do-${i}`}
                initial={{ opacity: 0, y: 8 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={inView}
                className="rounded-lg p-3 grid grid-cols-[56px,1fr] gap-3"
                style={{ border: '1px solid var(--rule)' }}
              >
                <div className="h-14 w-14 rounded-lg grid place-items-center" style={{ background: 'rgba(31,36,33,.05)' }}>
                  <it.Illustr />
                </div>
                <div>
                  <div className="text-sm font-semibold" style={{ color: 'var(--ink)' }}>{it.title}</div>
                  <p className="text-xs mt-0.5" style={{ color: 'var(--ink-muted)' }}>{it.desc}</p>
                </div>
              </motion.li>
            ))}
          </ul>
        </div>

        {/* DON'T column */}
        <div className="rounded-xl p-4" style={{ border: '1px solid var(--rule)' }}>
          <div className="flex items-center gap-2">
            <CrossBadge />
            <h3 className="text-sm font-semibold tracking-wider uppercase" style={{ color: 'var(--ink)' }}>Don&apos;t</h3>
          </div>
          <ul className="mt-3 grid gap-3">
            {dontItems.map((it, i) => (
              <motion.li
                key={`dont-${i}`}
                initial={{ opacity: 0, y: 8 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={inView}
                className="rounded-lg p-3 grid grid-cols-[56px,1fr] gap-3"
                style={{ border: '1px solid var(--rule)' }}
              >
                <div className="h-14 w-14 rounded-lg grid place-items-center" style={{ background: 'rgba(31,36,33,.05)' }}>
                  <it.Illustr />
                </div>
                <div>
                  <div className="text-sm font-semibold" style={{ color: 'var(--ink)' }}>{it.title}</div>
                  <p className="text-xs mt-0.5" style={{ color: 'var(--ink-muted)' }}>{it.desc}</p>
                </div>
              </motion.li>
            ))}
          </ul>
        </div>
      </div>

      {/* Legend */}
      <div className="mt-4 flex items-center gap-3 text-[12px]" style={{ color: 'var(--ink-muted)' }}>
        <CheckBadge small />
        <span>Recommended</span>
        <span className="opacity-30">•</span>
        <CrossBadge small />
        <span>Avoid</span>
      </div>
    </section>
  );
}

type Item = {
  title: string;
  desc: string;
  Illustr: React.FC;
};

function CheckBadge({ small = false }: { small?: boolean }) {
  return (
    <span
      className={["inline-grid place-items-center rounded-full", small ? "w-5 h-5" : "w-7 h-7"].join(" ")}
      style={{ background: 'rgba(42,164,166,.12)' }}
    >
      <svg width={small ? 16 : 18} height={small ? 16 : 18} viewBox="0 0 24 24" fill="none">
        <circle cx="12" cy="12" r="10" stroke="#2AA4A6" strokeWidth="2" />
        <path d="M7 12.5l3 3 7-7" stroke="#1A6F70" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </span>
  );
}

function CrossBadge({ small = false }: { small?: boolean }) {
  return (
    <span
      className={["inline-grid place-items-center rounded-full", small ? "w-5 h-5" : "w-7 h-7"].join(" ")}
      style={{ background: 'rgba(244,168,142,.12)' }}
    >
      <svg width={small ? 16 : 18} height={small ? 16 : 18} viewBox="0 0 24 24" fill="none">
        <circle cx="12" cy="12" r="10" stroke="#F4A88E" strokeWidth="2" />
        <path d="M8 8l8 8M16 8l-8 8" stroke="#F4A88E" strokeWidth="2.5" strokeLinecap="round" />
      </svg>
    </span>
  );
}

function LightGood({ float }: { float: Transition }) {
  return (
    <motion.svg width="36" height="36" viewBox="0 0 36 36" initial={{ y: 1 }} animate={{ y: -1 }} transition={float} aria-hidden>
      <circle cx="18" cy="18" r="8" fill="rgba(244,168,142,.80)" />
      <g stroke="rgba(244,168,142,.90)" strokeLinecap="round">
        <line x1="18" y1="2" x2="18" y2="6" />
        <line x1="18" y1="30" x2="18" y2="34" />
        <line x1="2" y1="18" x2="6" y2="18" />
        <line x1="30" y1="18" x2="34" y2="18" />
        <line x1="6.5" y1="6.5" x2="9.5" y2="9.5" />
        <line x1="26.5" y1="26.5" x2="29.5" y2="29.5" />
        <line x1="26.5" y1="9.5" x2="29.5" y2="6.5" />
        <line x1="6.5" y1="29.5" x2="9.5" y2="26.5" />
      </g>
    </motion.svg>
  );
}

function EyeLevelGood({ float }: { float: Transition }) {
  return (
    <motion.svg width="36" height="36" viewBox="0 0 36 36" initial={{ y: 1 }} animate={{ y: -1 }} transition={float} aria-hidden>
      <rect x="6" y="4" width="16" height="28" rx="3" fill="rgba(31,36,33,.12)" stroke="rgba(31,36,33,.28)" />
      <circle cx="14" cy="14" r="5" fill="rgba(42,164,166,.45)" />
      <rect x="9" y="20" width="10" height="8" rx="2" fill="rgba(42,164,166,.30)" />
      <circle cx="14" cy="7" r="0.8" fill="rgba(31,36,33,.28)" />
    </motion.svg>
  );
}

function BgGood() {
  return (
    <svg width="36" height="36" viewBox="0 0 36 36" aria-hidden>
      <rect x="3" y="6" width="30" height="24" rx="3" fill="#F8F4EE" stroke="rgba(31,36,33,.12)" />
      <g fill="rgba(31,36,33,.12)">
        {[0, 1, 2].map(r =>
          [0, 1, 2, 3].map(c => <rect key={`${r}-${c}`} x={6 + c * 7} y={8 + r * 7} width="5" height="5" rx="1.5" />)
        )}
      </g>
    </svg>
  );
}

function LensGood({ float }: { float: Transition }) {
  return (
    <motion.svg width="36" height="36" viewBox="0 0 36 36" initial={{ rotate: -2 }} animate={{ rotate: 2 }} transition={float} aria-hidden>
      <circle cx="18" cy="18" r="8" fill="rgba(42,164,166,.18)" stroke="rgba(42,164,166,.40)" />
      <path d="M10,24 Q18,16 26,24" fill="none" stroke="#2AA4A6" strokeWidth="2" />
      <path d="M22,12 L24,12 M23,11 L23,13" stroke="#1A6F70" strokeWidth="2" strokeLinecap="round" />
    </motion.svg>
  );
}

function BacklightBad() {
  return (
    <svg width="36" height="36" viewBox="0 0 36 36" aria-hidden>
      <rect x="4" y="6" width="28" height="14" rx="2" fill="rgba(244,168,142,.28)" stroke="rgba(244,168,142,.65)" />
      <circle cx="18" cy="26" r="6" fill="rgba(31,36,33,.12)" stroke="rgba(31,36,33,.16)" />
      <rect x="14" y="28" width="8" height="4" rx="2" fill="rgba(31,36,33,.12)" />
    </svg>
  );
}

function AngleBad() {
  return (
    <svg width="36" height="36" viewBox="0 0 36 36" aria-hidden>
      <g transform="rotate(-18 18 18)">
        <rect x="8" y="6" width="16" height="24" rx="3" fill="rgba(244,168,142,.15)" stroke="rgba(244,168,142,.42)" />
        <circle cx="16" cy="14" r="5" fill="rgba(244,168,142,.28)" />
        <rect x="11" y="20" width="10" height="6" rx="2" fill="rgba(244,168,142,.28)" />
      </g>
    </svg>
  );
}

function ObstructionBad() {
  return (
    <svg width="36" height="36" viewBox="0 0 36 36" aria-hidden>
      <circle cx="18" cy="16" r="6" fill="rgba(31,36,33,.12)" stroke="rgba(31,36,33,.16)" />
      <rect x="13" y="22" width="10" height="6" rx="2" fill="rgba(31,36,33,.12)" />
      <path d="M6,10 Q14,14 22,10" stroke="#F4A88E" strokeWidth="3" />
      <path d="M8,26 L28,6" stroke="#F4A88E" strokeWidth="2.5" strokeLinecap="round" />
    </svg>
  );
}

function FilterBad() {
  return (
    <svg width="36" height="36" viewBox="0 0 36 36" aria-hidden>
      <rect x="5" y="7" width="26" height="22" rx="4" fill="rgba(244,168,142,.18)" stroke="rgba(244,168,142,.40)" />
      <g transform="translate(9,11)">
        <rect x="0" y="8" width="12" height="3" rx="1.5" fill="#F4A88E" />
        <rect x="5" y="0" width="3" height="20" rx="1.5" fill="#F4A88E" />
        <path d="M16,6 L20,6 M18,4 L18,8" stroke="#F4A88E" strokeWidth="1.6" strokeLinecap="round" />
      </g>
      <path d="M10,26 L26,10 M26,26 L10,10" stroke="#F4A88E" strokeWidth="2.2" strokeLinecap="round" />
    </svg>
  );
}
